// src/pages/business/bookings/NewBooking.tsx
// Web adaptation of mobile NewBookingScreen - Create new service bookings

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft, User, Plus, Calendar, Clock, ChevronDown,
  Loader2, AlertCircle, CheckCircle, Search
} from 'lucide-react';
import apiClient from '@/services/apiClient';
import { useToast } from '@/contexts/ToastContext';
import { useAuth } from '@/contexts/AuthContext';

interface Service {
  id: string;
  name: string;
  price: number;
  duration_minutes?: number;
  category?: string;
}

interface StaffMember {
  id: string;
  full_name: string;
  role?: string;
  email?: string;
}

const NewBooking: React.FC = () => {
  const { businessId } = useParams<{ businessId: string }>();
  const navigate = useNavigate();
  const { success: toastSuccess, error: toastError } = useToast();
  const { user } = useAuth();

  // Form State
  const [clientName, setClientName] = useState('');
  const [clientContact, setClientContact] = useState('');
  const [selectedService, setSelectedService] = useState<Service | null>(null);
  const [selectedStaff, setSelectedStaff] = useState<StaffMember | null>(null);
  const [bookingDate, setBookingDate] = useState('');
  const [bookingTime, setBookingTime] = useState('');
  const [notes, setNotes] = useState('');

  // Dropdowns & Loading
  const [services, setServices] = useState<Service[]>([]);
  const [staff, setStaff] = useState<StaffMember[]>([]);
  const [showServiceDropdown, setShowServiceDropdown] = useState(false);
  const [showStaffDropdown, setShowStaffDropdown] = useState(false);
  const [serviceSearch, setServiceSearch] = useState('');
  const [staffSearch, setStaffSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchServices();
    fetchStaff();
  }, [businessId]);

  const fetchServices = async () => {
    try {
      const res = await apiClient.get(`/businesses/services/${businessId}`);
      setServices(res.data || []);
    } catch (error) {
      console.error('Failed to fetch services:', error);
    }
  };

  const fetchStaff = async () => {
    try {
      const res = await apiClient.get(`/businesses/${businessId}/staff`);
      setStaff(res.data.staff || []);
    } catch (error) {
      console.error('Failed to fetch staff:', error);
    }
  };

  const handleCreateBooking = async () => {
    if (!clientName.trim()) {
      toastError('Please enter client name');
      return;
    }
    if (!selectedService) {
      toastError('Please select a service');
      return;
    }
    if (!selectedStaff) {
      toastError('Please assign a staff member');
      return;
    }
    if (!bookingDate) {
      toastError('Please select a booking date');
      return;
    }
    if (!bookingTime) {
      toastError('Please select a booking time');
      return;
    }

    setSubmitting(true);
    try {
      await apiClient.post(`/businesses/${businessId}/bookings`, {
        customer_name: clientName,
        customer_phone: clientContact,
        service_id: selectedService.id,
        staff_id: selectedStaff.id,
        booking_date: bookingDate,
        booking_time: bookingTime,
        notes,
      });
      toastSuccess('Booking created successfully');
      navigate(`/business/${businessId}/bookings`);
    } catch (error: any) {
      console.error('Failed to create booking:', error);
      toastError(error.response?.data?.error || 'Failed to create booking');
    } finally {
      setSubmitting(false);
    }
  };

  const handleAssignToMe = () => {
    if (user) {
      // Find staff member matching current user
      const myStaff = staff.find(s => s.email === user.email || s.full_name === user.user_metadata?.full_name);
      if (myStaff) {
        setSelectedStaff(myStaff);
      } else {
        // Create a mock staff object for the current user
        setSelectedStaff({
          id: user.id,
          full_name: user.user_metadata?.full_name || 'Me',
          role: 'Owner',
        });
      }
    }
  };

  const filteredServices = services.filter(s =>
    s.name.toLowerCase().includes(serviceSearch.toLowerCase())
  );

  const filteredStaff = staff.filter(s =>
    s.full_name.toLowerCase().includes(staffSearch.toLowerCase())
  );

  const getTomorrowDate = () => {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    return tomorrow.toISOString().split('T')[0];
  };

  return (
    <div className="min-h-screen bg-light-bg dark:bg-dark-bg pb-24">
      {/* Header */}
      <div className="sticky top-0 z-40 bg-light-card dark:bg-dark-card border-b border-light-border dark:border-dark-border">
        <div className="max-w-5xl mx-auto flex items-center gap-4 px-4 py-4">
          <button
            onClick={() => navigate(`/business/${businessId}/bookings`)}
            className="p-2 rounded-lg hover:bg-light-bg dark:hover:bg-dark-bg transition-colors"
          >
            <ArrowLeft size={24} style={{ color: '#4F46E5' }} />
          </button>
          <h1 className="text-lg font-bold text-light-text dark:text-dark-text">Create New Booking</h1>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-5xl mx-auto px-4 py-6 space-y-6">
        {/* Client Information */}
        <div className="bg-light-card dark:bg-dark-card rounded-xl p-5 shadow-sm">
          <h3 className="font-semibold text-sm mb-4 text-light-text dark:text-dark-text flex items-center gap-2">
            <User size={18} style={{ color: '#4F46E5' }} />
            Client Information
          </h3>
          <div className="space-y-4">
            <div>
              <label className="block text-xs font-medium text-light-subtle dark:text-dark-subtle mb-2">
                Client Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={clientName}
                onChange={(e) => setClientName(e.target.value)}
                placeholder="Enter client's full name"
                className="w-full px-4 py-3 rounded-xl bg-light-bg dark:bg-dark-bg border border-light-border dark:border-dark-border text-sm text-light-text dark:text-dark-text focus:outline-none focus:ring-2 focus:ring-primary/20"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-light-subtle dark:text-dark-subtle mb-2">
                Client Phone / Email
              </label>
              <input
                type="text"
                value={clientContact}
                onChange={(e) => setClientContact(e.target.value)}
                placeholder="Enter client's contact info"
                className="w-full px-4 py-3 rounded-xl bg-light-bg dark:bg-dark-bg border border-light-border dark:border-dark-border text-sm text-light-text dark:text-dark-text focus:outline-none focus:ring-2 focus:ring-primary/20"
              />
            </div>
          </div>
        </div>

        {/* Service & Staff Selection */}
        <div className="bg-light-card dark:bg-dark-card rounded-xl p-5 shadow-sm">
          <h3 className="font-semibold text-sm mb-4 text-light-text dark:text-dark-text">Service & Staff</h3>
          <div className="space-y-4">
            {/* Service Selection */}
            <div>
              <label className="block text-xs font-medium text-light-subtle dark:text-dark-subtle mb-2">
                Service <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <button
                  type="button"
                  onClick={() => { setShowServiceDropdown(!showServiceDropdown); setShowStaffDropdown(false); }}
                  className="w-full flex items-center justify-between px-4 py-3 rounded-xl bg-light-bg dark:bg-dark-bg border border-light-border dark:border-dark-border text-sm focus:outline-none focus:ring-2 focus:ring-primary/20"
                >
                  <span className={selectedService ? 'text-light-text dark:text-dark-text' : 'text-light-subtle dark:text-dark-subtle'}>
                    {selectedService ? selectedService.name : 'Select a service'}
                  </span>
                  <ChevronDown size={18} className="text-light-subtle dark:text-dark-subtle" />
                </button>

                {showServiceDropdown && (
                  <div className="absolute z-50 w-full mt-2 bg-light-card dark:bg-dark-card border border-light-border dark:border-dark-border rounded-xl shadow-lg max-h-64 overflow-hidden">
                    <div className="p-3 border-b border-light-border dark:border-dark-border">
                      <div className="relative">
                        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-light-subtle dark:text-dark-subtle" />
                        <input
                          type="text"
                          value={serviceSearch}
                          onChange={(e) => setServiceSearch(e.target.value)}
                          placeholder="Search services..."
                          className="w-full pl-9 pr-4 py-2 rounded-lg bg-light-bg dark:bg-dark-bg text-sm text-light-text dark:text-dark-text focus:outline-none"
                        />
                      </div>
                    </div>
                    <div className="overflow-y-auto max-h-48">
                      {filteredServices.length === 0 ? (
                        <div className="p-4 text-center text-sm text-light-subtle dark:text-dark-subtle">
                          No services found
                        </div>
                      ) : (
                        filteredServices.map((service) => (
                          <button
                            key={service.id}
                            type="button"
                            onClick={() => {
                              setSelectedService(service);
                              setShowServiceDropdown(false);
                              setServiceSearch('');
                            }}
                            className={`w-full flex items-center justify-between px-4 py-3 text-left hover:bg-light-bg dark:hover:bg-dark-bg transition-colors ${
                              selectedService?.id === service.id ? 'bg-primary/10 dark:bg-primary/20' : ''
                            }`}
                          >
                            <div>
                              <p className="font-medium text-sm text-light-text dark:text-dark-text">{service.name}</p>
                              <p className="text-xs text-light-subtle dark:text-dark-subtle">
                                KES {service.price.toLocaleString()}
                                {service.duration_minutes && ` • ${service.duration_minutes} min`}
                              </p>
                            </div>
                            {selectedService?.id === service.id && (
                              <CheckCircle size={16} className="text-primary" />
                            )}
                          </button>
                        ))
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Staff Selection */}
            <div>
              <label className="block text-xs font-medium text-light-subtle dark:text-dark-subtle mb-2">
                Assign Staff <span className="text-red-500">*</span>
              </label>
              <div className="flex gap-3">
                <div className="flex-1 relative">
                  <button
                    type="button"
                    onClick={() => { setShowStaffDropdown(!showStaffDropdown); setShowServiceDropdown(false); }}
                    className="w-full flex items-center justify-between px-4 py-3 rounded-xl bg-light-bg dark:bg-dark-bg border border-light-border dark:border-dark-border text-sm focus:outline-none focus:ring-2 focus:ring-primary/20"
                  >
                    <span className={selectedStaff ? 'text-light-text dark:text-dark-text' : 'text-light-subtle dark:text-dark-subtle'}>
                      {selectedStaff ? selectedStaff.full_name : 'Select staff'}
                    </span>
                    <ChevronDown size={18} className="text-light-subtle dark:text-dark-subtle" />
                  </button>

                  {showStaffDropdown && (
                    <div className="absolute z-50 w-full mt-2 bg-light-card dark:bg-dark-card border border-light-border dark:border-dark-border rounded-xl shadow-lg max-h-64 overflow-hidden">
                      <div className="p-3 border-b border-light-border dark:border-dark-border">
                        <div className="relative">
                          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-light-subtle dark:text-dark-subtle" />
                          <input
                            type="text"
                            value={staffSearch}
                            onChange={(e) => setStaffSearch(e.target.value)}
                            placeholder="Search staff..."
                            className="w-full pl-9 pr-4 py-2 rounded-lg bg-light-bg dark:bg-dark-bg text-sm text-light-text dark:text-dark-text focus:outline-none"
                          />
                        </div>
                      </div>
                      <div className="overflow-y-auto max-h-48">
                        {filteredStaff.length === 0 ? (
                          <div className="p-4 text-center text-sm text-light-subtle dark:text-dark-subtle">
                            No staff members found
                          </div>
                        ) : (
                          filteredStaff.map((member) => (
                            <button
                              key={member.id}
                              type="button"
                              onClick={() => {
                                setSelectedStaff(member);
                                setShowStaffDropdown(false);
                                setStaffSearch('');
                              }}
                              className={`w-full flex items-center justify-between px-4 py-3 text-left hover:bg-light-bg dark:hover:bg-dark-bg transition-colors ${
                                selectedStaff?.id === member.id ? 'bg-primary/10 dark:bg-primary/20' : ''
                              }`}
                            >
                              <div>
                                <p className="font-medium text-sm text-light-text dark:text-dark-text">{member.full_name}</p>
                                <p className="text-xs text-light-subtle dark:text-dark-subtle">{member.role || 'Staff'}</p>
                              </div>
                              {selectedStaff?.id === member.id && (
                                <CheckCircle size={16} className="text-primary" />
                              )}
                            </button>
                          ))
                        )}
                      </div>
                    </div>
                  )}
                </div>
                <button
                  type="button"
                  onClick={handleAssignToMe}
                  className="px-4 py-3 rounded-xl text-white font-semibold text-sm whitespace-nowrap"
                  style={{ backgroundColor: '#d1a02f' }}
                >
                  Assign to Me
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Date & Time Selection */}
        <div className="bg-light-card dark:bg-dark-card rounded-xl p-5 shadow-sm">
          <h3 className="font-semibold text-sm mb-4 text-light-text dark:text-dark-text flex items-center gap-2">
            <Calendar size={18} style={{ color: '#4F46E5' }} />
            Appointment Date & Time <span className="text-red-500">*</span>
          </h3>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-light-subtle dark:text-dark-subtle mb-2">
                Date
              </label>
              <div className="relative">
                <input
                  type="date"
                  value={bookingDate}
                  min={getTomorrowDate()}
                  onChange={(e) => setBookingDate(e.target.value)}
                  className="w-full px-4 py-3 rounded-xl bg-light-bg dark:bg-dark-bg border border-light-border dark:border-dark-border text-sm text-light-text dark:text-dark-text focus:outline-none focus:ring-2 focus:ring-primary/20"
                />
              </div>
            </div>
            <div>
              <label className="block text-xs font-medium text-light-subtle dark:text-dark-subtle mb-2">
                Time
              </label>
              <input
                type="time"
                value={bookingTime}
                onChange={(e) => setBookingTime(e.target.value)}
                className="w-full px-4 py-3 rounded-xl bg-light-bg dark:bg-dark-bg border border-light-border dark:border-dark-border text-sm text-light-text dark:text-dark-text focus:outline-none focus:ring-2 focus:ring-primary/20"
              />
            </div>
          </div>
        </div>

        {/* Notes */}
        <div className="bg-light-card dark:bg-dark-card rounded-xl p-5 shadow-sm">
          <h3 className="font-semibold text-sm mb-4 text-light-text dark:text-dark-text">Notes (Optional)</h3>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Add any additional notes about this booking..."
            rows={3}
            className="w-full px-4 py-3 rounded-xl bg-light-bg dark:bg-dark-bg border border-light-border dark:border-dark-border text-sm text-light-text dark:text-dark-text focus:outline-none focus:ring-2 focus:ring-primary/20 resize-none"
          />
        </div>

        {/* Submit Button */}
        <button
          onClick={handleCreateBooking}
          disabled={submitting}
          className="w-full flex items-center justify-center gap-2 px-6 py-4 rounded-xl text-white font-semibold text-lg transition-all hover:opacity-90 disabled:opacity-50"
          style={{ backgroundColor: '#4F46E5' }}
        >
          {submitting ? (
            <>
              <Loader2 size={20} className="animate-spin" />
              Creating Booking...
            </>
          ) : (
            <>
              <CheckCircle size={20} />
              Create Booking
            </>
          )}
        </button>
      </div>

      {/* Click outside to close dropdowns */}
      {(showServiceDropdown || showStaffDropdown) && (
        <div
          className="fixed inset-0 z-40"
          onClick={() => { setShowServiceDropdown(false); setShowStaffDropdown(false); }}
        />
      )}
    </div>
  );
};

export default NewBooking;